<template>
  <b-sidebar
    id="add-new-user-sidebar"
    :visible="isAddNewUserSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="hideSidebar"
    @change="val => $emit('update:is-add-new-user-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          Add New User
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <validation-observer ref="refFormObserver">
        <b-form
          class="p-2"
          autocomplete="off"
          @submit.prevent="addNewUser"
          @reset.prevent="resetForm"
        >

          <validation-provider
            #default="validationContext"
            name="Full Name"
            rules="required"
          >
            <b-form-group
              label="Full Name"
              label-for="full-name"
            >
              <b-form-input
                id="full-name"
                v-model="userData.FullName"
                autofocus
                :state="getValidationState(validationContext)"
                trim
                placeholder="John Doe"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="Email"
            rules="required"
          >
            <b-form-group
              label="Username (Email)"
              label-for="Email"
            >
              <b-input-group>
                <b-form-input
                  id="Email"
                  v-model="userData.Email"
                  type="text"
                  autocomplete="off"
                  placeholder="nama@perusahaan.com"
                  :state="emailInputState(validationContext)"
                  trim
                />
                <b-input-group-append
                  v-if="emailStatus !== 'idle'"
                  is-text
                  class="email-status-append"
                >
                  <b-spinner
                    v-if="emailStatus === 'checking'"
                    small
                    variant="secondary"
                  />
                  <feather-icon
                    v-else-if="emailStatus === 'available'"
                    icon="CheckCircleIcon"
                    size="18"
                    class="text-success"
                  />
                  <feather-icon
                    v-else
                    icon="XCircleIcon"
                    size="18"
                    class="text-danger"
                  />
                </b-input-group-append>
              </b-input-group>
              <small
                v-if="emailMessage"
                class="d-block mt-50 email-status-message"
                :class="`text-${emailMessage.variant}`"
              >
                {{ emailMessage.text }}
              </small>
              <b-form-invalid-feedback
                v-else
                :state="false"
                class="d-block"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="Phone"
            rules="required"
          >
            <b-form-group
              label="Phone"
              label-for="phone"
            >
              <b-form-input
                id="phone"
                v-model="userData.PhoneNumber"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="User Role"
            rules="required"
          >
            <b-form-group
              label="User Role"
              label-for="RoleId"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="userData.RoleId"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="roles"
                :clearable="false"
                input-id="RoleId"
                @input="changeRoute"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            v-if="isDepartementShown"
            #default="validationContext"
            name="User Departement"
          >
            <b-form-group
              label="User Departement"
              label-for="DepartementID"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="userData.DepartementID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="departements"
                :reduce="val => val.value"
                :clearable="false"
                input-id="DepartementID"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            v-if="isShown"
            #default="validationContext"
            name="User Branch"
          >
            <b-form-group
              label="User Branch"
              label-for="BranchID"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="userData.BranchID"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="branches"
                :reduce="val => val.value"
                :clearable="false"
                input-id="BranchID"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            v-if="isShownRegion"
            #default="validationContext"
            name="Branch Region"
          >
            <b-form-group
              label="Regions"
              label-for="RegionIDs"
              :state="getValidationState(validationContext)"
            >
              <v-select-new
                v-model="userData.RegionIDs"
                name="RegionIDs"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="regions"
                :clearable="false"
                input-id="RegionIDs"
                multiple
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="Occupation"
            rules="required"
          >
            <b-form-group
              label="Occupation"
              label-for="Occupation"
            >
              <b-form-input
                id="Occupation"
                v-model="userData.Occupation"
                :state="getValidationState(validationContext)"
                trim
                placeholder="Occupation"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              block
              :disabled="submitButtonDisabled"
            >
              Add
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hideSidebar"
            >
              Cancel
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormInvalidFeedback, BButton,
  BInputGroup, BInputGroupAppend, BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, alphaNum } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import axios from 'axios'
import moment from 'moment-timezone'
import { ref } from '@vue/composition-api'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import apiEndpoints from '@/constants/apiEndpoints'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddNewUserSidebarActive',
    event: 'update:is-add-new-user-sidebar-active',
  },
  props: {
    isAddNewUserSidebarActive: {
      type: Boolean,
      required: true,
    },
    roleOptions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      submitButtonDisabled: false,
      isShown: false,
      isDepartementShown: false,
      isShownRegion: false,
      // 'idle' | 'invalid' | 'checking' | 'available' | 'taken' | 'error'
      emailStatus: 'idle',
      emailCheckTimer: null,
      emailCheckSeq: 0,

      required,
      alphaNum,

      roles: [],
      branches: [],
      departements: [],
      regions: [],
    }
  },
  setup() {
    const blankUserData = {
      FullName: '',
      Email: '',
      PhoneNumber: '',
      RoleId: null,
      DepartementID: null,
      BranchID: null,
      RegionIDs: [],
      Occupation: '',
    }

    const userData = ref(JSON.parse(JSON.stringify(blankUserData)))

    const resetUserData = () => {
      userData.value = JSON.parse(JSON.stringify(blankUserData))
    }

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetUserData)

    return {
      userData,
      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
  computed: {
    emailMessage() {
      switch (this.emailStatus) {
        case 'checking': return { variant: 'muted', text: 'Memeriksa ketersediaan email...' }
        case 'available': return { variant: 'success', text: 'Email tersedia, belum pernah didaftarkan.' }
        case 'taken': return { variant: 'danger', text: 'Email sudah terdaftar. Gunakan email lain.' }
        case 'invalid': return { variant: 'danger', text: 'Format email tidak valid.' }
        case 'error': return { variant: 'warning', text: 'Gagal memeriksa email. Akan dicek ulang saat disimpan.' }
        default: return null
      }
    },
  },
  watch: {
    'userData.Email': function onEmailChange(value) {
      this.scheduleEmailCheck(value)
    },
  },
  mounted() {
    this.getRoles()
    this.getBranches()
    this.getDepartements()
    this.getRegions()
  },
  beforeDestroy() {
    clearTimeout(this.emailCheckTimer)
  },
  methods: {
    hideSidebar() {
      this.$emit('update:is-add-new-user-sidebar-active', false)
      this.resetForm()
      this.isShown = false
      this.isDepartementShown = false
      this.isShownRegion = false
    },
    getRegions() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      axios
        .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_REGION}`, { headers })
        .then(response => {
          if (response.data && response.data.data) {
            this.regions = response.data.data.map(elem => ({
              label: elem.region_name,
              value: elem.region_id,
              slug: elem.region_slug,
            }))
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    getBranches() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      axios
        .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_BRANCHES}`, { headers })
        .then(response => {
          if (response.data && response.data.data) {
            const branchesJoin = response.data.data.map(elem => ({
              label: `(${elem.branch_code}) ${elem.branch_address}`,
              value: elem.branch_id,
              kodeunit: elem.branch_code,
            }))
            this.branches = branchesJoin.sort((a, b) => (parseInt(a.kodeunit, 10) > parseInt(b.kodeunit, 10) ? 1 : -1))
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    getRoles() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      axios
        .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_ROLES}`, { headers })
        .then(response => {
          if (response.data && response.data.data) {
            this.roles = response.data.data.map(elem => ({
              label: elem.role_name,
              value: elem.role_id,
              slug: elem.role_slug,
            }))
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    getDepartements() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      axios
        .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_DEPARTEMENTS}`, { headers })
        .then(response => {
          if (response.data && response.data.data) {
            this.departements = response.data.data.map(elem => ({
              label: elem.departement_name,
              value: elem.departement_id,
            }))
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    changeRoute(selectedRole) {
      if (!selectedRole || !selectedRole.slug) {
        this.isShown = false
        this.isDepartementShown = false
        this.isShownRegion = false
        return
      }

      // THIS IS THE FIX FOR THE LINTER
      const { slug } = selectedRole

      const branchSlugs = [
        'user', 'stock-manager', 'purchasing-cabang', 'laboratory-manager',
        'assistant-lab-manager', 'supervisor-cabang', 'warehouse-cabang', 'finance',
      ]

      this.isShown = branchSlugs.includes(slug)
      this.isDepartementShown = ['user', 'supervisor-cabang'].includes(slug)
      this.isShownRegion = slug === 'regional-manager'
    },
    addNewUser() {
      this.$refs.refFormObserver.validate().then(success => {
        if (!success) return
        if (['taken', 'invalid', 'checking'].includes(this.emailStatus)) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Email belum valid',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: this.emailMessage ? this.emailMessage.text : 'Periksa kembali email.',
            },
          })
          return
        }

        this.submitButtonDisabled = true
        const userToken = this.$cookies.get('userToken')
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        }

        const payload = {
          user_username: this.userData.Email,
          user_fullname: this.userData.FullName,
          user_phone: this.userData.PhoneNumber,
          user_role_id: this.userData.RoleId ? this.userData.RoleId.value : null,
          user_departement_id: this.userData.DepartementID,
          user_branch_id: this.userData.BranchID,
          user_occupation: this.userData.Occupation,
          regions: this.userData.RegionIDs,
          time: moment().format(),
        }

        const url = 'https://api-proc.geolims.com/users/new'

        axios
          .post(url, payload, { headers })
          .then(response => {
            this.submitButtonDisabled = false
            if (response.data.success === true) {
              this.hideSidebar()
              this.$root.$emit('refreshTable', 'refreshTable')
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Success',
                  icon: 'CheckIcon',
                  variant: 'success',
                  text: 'User has been successfully added!',
                },
              })
            } else {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Notification',
                  icon: 'BellIcon',
                  variant: 'danger',
                  text: response.data.message || 'Validation failed on server.',
                },
              })
            }
          })
          .catch(error => {
            console.error(error)
            this.submitButtonDisabled = false
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Error',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: 'Something went wrong during submission.',
              },
            })
          })
      })
    },
    emailInputState(validationContext) {
      if (this.emailStatus === 'available') return true
      if (['taken', 'invalid'].includes(this.emailStatus)) return false
      if (this.emailStatus === 'checking') return null
      return this.getValidationState(validationContext)
    },
    scheduleEmailCheck(value) {
      clearTimeout(this.emailCheckTimer)
      this.emailCheckSeq += 1
      const email = (value || '').trim()
      if (!email) {
        this.emailStatus = 'idle'
        return
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.emailStatus = 'invalid'
        return
      }
      this.emailStatus = 'checking'
      const seq = this.emailCheckSeq
      this.emailCheckTimer = setTimeout(() => this.checkEmail(email, seq), 500)
    },
    checkEmail(email, seq) {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.$cookies.get('userToken')}`,
      }
      axios
        .get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_CHECK_USERNAME}`, {
          headers,
          params: { username: email },
        })
        .then(response => {
          if (seq !== this.emailCheckSeq) return // stale: user kept typing
          this.emailStatus = response.data.data && response.data.data.available ? 'available' : 'taken'
        })
        .catch(() => {
          if (seq !== this.emailCheckSeq) return
          this.emailStatus = 'error'
        })
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-new-user-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }

  .email-status-append .input-group-text {
    background: transparent;
    min-width: 42px;
    justify-content: center;
  }

  .email-status-message {
    transition: color 0.2s ease;
  }
}
</style>